import type { FC, } from "react";
import styled from "styled-components";

export type FourOhFourProps = {
	title?: string;
	message?: string;
	onGoHome?: () => void;
}


/**
 * A 404 error page component that displays when a route is not found
 * 
 * @param props - The component props
 * @param props.title - Optional custom title for the 404 page
 * @param props.message - Optional custom message for the 404 page
 * @param props.onGoHome - Optional callback function when home button is clicked
 * @returns JSX element representing the 404 page
 */
export const FourOhFour: FC<FourOhFourProps> = ({ 
	title = "404 - Page Not Found",
	message = "The page you're looking for doesn't exist or has been moved.",
	onGoHome
}) => {
	
	const handleGoHome = () => {
		if (onGoHome) {
			onGoHome();
		} else {
			window.location.href = "/";
		}
	};

	return (
		<Container>
			<Content>
				<ErrorCode>404</ErrorCode>
				<Title>{title}</Title>
				<Message>{message}</Message>
				<ActionButtons>
					<HomeButton onClick={handleGoHome}>
						Go Home
					</HomeButton>
					<BackButton onClick={() => window.history.back()}>
						Go Back
					</BackButton>
				</ActionButtons>
			</Content>
		</Container>
	);
};


const Container = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	padding: 2rem;
	background: var(--background-primary);
`;


const Content = styled.div`
	text-align: center;
	max-width: 500px;
	width: 100%;
`;


const ErrorCode = styled.h1`
	font-size: 8rem;
	font-weight: 700;
	color: var(--primary-color);
	margin: 0;
	line-height: 1;
	font-family: 'Space Mono', monospace;
	
	@media (max-width: 768px) {
		font-size: 6rem;
	}
`;


const Title = styled.h2`
	font-size: 2rem;
	font-weight: 600;
	color: var(--text-heading);
	margin: 1rem 0;
	font-family: 'Space Mono', monospace;
	
	@media (max-width: 768px) {
		font-size: 1.5rem;
	}
`;


const Message = styled.p`
	font-size: 1.125rem;
	color: var(--text-paragraph);
	margin: 1.5rem 0 2.5rem;
	line-height: 1.6;
`;


const ActionButtons = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
	flex-wrap: wrap;
`;


const HomeButton = styled.button`
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	border: none;
	background: var(--primary-color);
	color: var(--text-on-primary);
	font-family: 'Space Mono', monospace;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: var(--primary-color-hover);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;


const BackButton = styled.button`
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	border: 1px solid var(--border-primary);
	background: transparent;
	color: var(--text-paragraph);
	font-family: 'Space Mono', monospace;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: var(--background-hover);
		border-color: var(--border-hover);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;
